import { useCallback, useEffect, useMemo } from 'react'
import { useMatch, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import {
  selectWorkspaces,
  selectCurrentWorkspace,
  selectCalendarEvents,
  selectRepeatingEvents,
  selectHasFetchedWorkspaces,
  setCurrentWorkspace,
} from '../slices/workspaceSlice'

export const useCurrentWorkspace = (selectedWorkspaceId?: string) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const workspaceSubPath = useMatch('/workspace/:workspaceId/:subPath')

  const hasFetchedWorkspaces = useSelector(selectHasFetchedWorkspaces)
  const myWorkspaces = useSelector(selectWorkspaces)
  const currentWorkspace = useSelector(selectCurrentWorkspace)
  const allCalendarEvents = useSelector(selectCalendarEvents)
  const allRepeatingEvents = useSelector(selectRepeatingEvents)

  const workspace = useMemo(() => {
    if (currentWorkspace) {
      return myWorkspaces.find((w) => w.id === currentWorkspace)
    }
    return null
  }, [myWorkspaces, currentWorkspace])

  const calendarEvents = useMemo(() => {
    if (currentWorkspace) {
      return allCalendarEvents[currentWorkspace] ?? []
    }
    return []
  }, [allCalendarEvents, currentWorkspace])

  const repeatingEvents = useMemo(() => {
    if (currentWorkspace) {
      return allRepeatingEvents[currentWorkspace] ?? []
    }
    return []
  }, [allRepeatingEvents, currentWorkspace])

  const onSetCurrentWorkspace = useCallback(
    (workspaceId: string) => {
      dispatch(setCurrentWorkspace({ id: workspaceId }))
      if (workspaceSubPath) {
        navigate(
          `/workspace/${workspaceId}/${workspaceSubPath.params.subPath}/`,
        )
      } else {
        navigate(`/workspace/${workspaceId}/calendar/`)
      }
    },
    [dispatch, navigate, workspaceSubPath],
  )

  useEffect(() => {
    if (selectedWorkspaceId) {
      dispatch(setCurrentWorkspace({ id: selectedWorkspaceId }))
    }
  }, [selectedWorkspaceId, dispatch])

  return {
    workspace,
    hasFetchedWorkspaces,
    myWorkspaces,
    calendarEvents,
    repeatingEvents,
    onSetCurrentWorkspace,
  }
}
